import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular, Save20Regular } from "@fluentui/react-icons";
import "../CSS/ListQueuesComponent.css";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { DeleteQueue, GetQueue } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useMsal } from "@azure/msal-react";
import { useAbility } from "../../Contexts/AbilityContext";
import InfoButton from "../GenericComponent/InfoButton";
import { useNotification } from '../GenericComponent/NotificationSystem';

const ListQueuesComponent = ({ QUEUES, setQueues, ACTIVE_TENANT, SCOPES, SCOPES_NAMES }) => {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();

  const ability = useAbility();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [queuesRows, setQueuesRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    NAME: false,
    ENTITY: false,
    AGENT_HUNT_STRATEGY: false,
    QUEUE_SIZE: false,
    MAX_WAIT_TIME: false,
    NUMBER_OF_AGENT: false,
  });
  const [filters, setFilters] = useState({
    NAME: "",
    ENTITY: "",
    AGENT_HUNT_STRATEGY: "",
    QUEUE_SIZE: "",
    MAX_WAIT_TIME: "",
    NUMBER_OF_AGENT: "",
  });
  // --- Colonnes et tri
  const columns = [
    createTableColumn({
      columnId: "NAME",
      compare: (a, b) => a.queueName?.localeCompare(b.queueName || "") ?? 0,
    }),
    createTableColumn({
      columnId: "ENTITY",
      compare: (a, b) => a.scope?.localeCompare(b.scope || "") ?? 0,
    }),
    createTableColumn({
      columnId: "AGENT_HUNT_STRATEGY",
      compare: (a, b) => a.huntMethod?.localeCompare(b.huntMethod || "") ?? 0,
    }),
    createTableColumn({
      columnId: "QUEUE_SIZE",
      compare: (a, b) => (a.maxInQueue ?? 0) - (b.maxInQueue ?? 0),
    }),
    createTableColumn({
      columnId: "MAX_WAIT_TIME",
      compare: (a, b) => (a.maxWaitTime ?? 0) - (b.maxWaitTime ?? 0),
    }),
    createTableColumn({
      columnId: "NUMBER_OF_AGENT",
      compare: (a, b) => (a.numberOfAgents ?? 0) - (b.numberOfAgents ?? 0),
    }),
    createTableColumn({ columnId: "ACTION" }),
  ];
  // ⚡ Initialisation des rows avec permissions
  useEffect(() => {
    if (!QUEUES || !ability) return;

    const queuesArray = Object.values(QUEUES);

    const updatedRows = queuesArray.map((queue) => {
      const scope = queue.scope;
  
    
      
      // 🎯 Calculer toutes les permissions granulaires par section
      const permissions = {
        main: {
          canCreate: ability.can("create", `Queue.main@${scope}`),
          canRead: ability.can("read", `Queue.main@${scope}`),
          canUpdate: ability.can("update", `Queue.main@${scope}`),
          canDelete: ability.can("delete", `Queue.main@${scope}`),
        },
     
      };
      
      return {
        ...queue,
        numberOfAgents: queue.agentsIds?.length ?? 0,
        // Ajouter l'objet permissions complet
        permissions,
      };
    });
    setRows(updatedRows);
    setFilteredRows(updatedRows.filter((q) => q.permissions?.main?.canRead));
  }, [QUEUES, ability]);

  // --- Fonction de filtrage externe ---
  const applyFilters = (rows, filters) => {
    return rows.filter((queue) => {
      // 🔹 Filtre textuels
      const matchName =
        !filters.NAME ||
        queue.queueName?.toLowerCase().includes(filters.NAME.toLowerCase());

      const matchEntity =
        !filters.ENTITY ||
        queue.scope?.toLowerCase().includes(filters.ENTITY.toLowerCase());

      const matchAgentHuntStrategy =
        !filters.AGENT_HUNT_STRATEGY ||
        queue.huntMethod
          ?.toLowerCase()
          .includes(filters.AGENT_HUNT_STRATEGY.toLowerCase());

      // 🔹 Filtre sur des nombres (convertis en texte uniquement pour includes)
      const matchQueueSize =
        !filters.QUEUE_SIZE ||
        String(queue.maxInQueue ?? "").includes(filters.QUEUE_SIZE);

      const matchMaxWaitTime =
        !filters.MAX_WAIT_TIME ||
        String(queue.maxWaitTime ?? "").includes(filters.MAX_WAIT_TIME);

      // 🔹 Filtre sur le nombre d’agents
      const numberOfAgents = queue.agentsIds ? queue.agentsIds.length : 0;
      const matchNumberOfAgent =
        !filters.NUMBER_OF_AGENT ||
        String(numberOfAgents).includes(filters.NUMBER_OF_AGENT);

      // 🔹 Retourne true si tous les filtres sont valides
      return (
        matchName &&
        matchEntity &&
        matchAgentHuntStrategy &&
        matchQueueSize &&
        matchMaxWaitTime &&
        matchNumberOfAgent
      );
    });
  };

  // --- useEffect qui applique le filtre ---
  useEffect(() => {
    const newFiltered = applyFilters(rows, filters);
    setFilteredRows(newFiltered);
  }, [filters, rows]);

  // Poll backend for queue changes (agents removed on backend) and update counts
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    let stopped = false;
    const poll = async () => {
      try {
        const token = await getAccessToken(instance, activeAccount);
        await Promise.all(rows.map(async (r) => {
          try {
            const latest = await GetQueue(ACTIVE_TENANT, userId, r.id || r.item?.id, r.scope || r.item?.scope, token);
            if (!latest) return;
            const latestCount = latest.agentsIds ? latest.agentsIds.length : 0;
            setRows(prev => prev.map(row => {
              const id = row.id || row.item?.id;
              if (id === latest.id && (row.numberOfAgents !== latestCount)) {
                return { ...row, numberOfAgents: latestCount, agentsIds: latest.agentsIds };
              }
              return row;
            }));
          } catch (err) {
            // ignore per-queue errors
            console.debug('Queue poll error for', r.id || r.item?.id, err?.message || err);
          }
        }));
      } catch (err) {
        console.debug('Queues polling error', err?.message || err);
      }
    };

    const iv = setInterval(() => { if (!stopped) poll(); }, 5000);
    // initial poll
    poll();
    return () => { stopped = true; clearInterval(iv); };
  }, [rows, instance, activeAccount, ACTIVE_TENANT, userId]);
  const { getRows, sort: { getSortDirection, toggleColumnSort, sort } } = useTableFeatures(
    { columns, items: filteredRows },
    [useTableSort({ defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" } })]
  );

  const sortedRows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = columnId => ({
    onClick: e => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleFilterChange = (col, value) => setFilters(prev => ({ ...prev, [col]: value }));
  const toggleFilterVisibility = col => setFilterVisible(prev => ({ ...prev, [col]: !prev[col] }));

  const confirmRemoveQueue = queue => setSelectedQueue(queue);

  const removeQueueHandler = async queue => {
    try {
      setLoadingDeleteId(queue.id);
      const token = await getAccessToken(instance, activeAccount);
      await DeleteQueue(ACTIVE_TENANT, userId, queue.id, queue.scope, token);
      setRows(prev => prev.filter(q => q.id !== queue.id));
      if (setQueues) setQueues(prev => {
        const copy = { ...prev };
        delete copy[queue.id];
        return copy;
      });
      setSelectedQueue(null);
      showSuccess(t("QUEUE_DELETED_SUCCESS"));
    } catch (err) {
      console.error("Erreur suppression queue:", err);
      showError(t("QUEUE_DELETE_ERROR"), err?.message || t("QUEUE_DELETE_FAILED"));
    } finally {
      setLoadingDeleteId(null);
    }
  };

  const addQueue = () => {
    navigate("/queues/newQueue");
  };

  const selectQueue = queue => {
    navigate(`/queues/${queue.id}`);
  };

  // 🔐 Vérifier les permissions via CASL ability plutôt que via les données
  // Récupérer tous les scopes disponibles (pas seulement ceux des queues existantes)
  const availableScopes = SCOPES_NAMES || [];
  
  // Vérifier si l'utilisateur peut créer des queues dans au moins un scope
  const hasAnyCreatePermission = availableScopes.some(scope => 
    ability?.can("create", `Queue.main@${scope}`)
  );

  return (
    <>
      <div >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{t("QUEUES_LIST")}</h2>
        <Button 
          appearance="primary" 
          onClick={addQueue} 
          disabled={!hasAnyCreatePermission} 
          className="queue-list-button-style"
        >
          <Add20Filled /> {t("ADD_NEW_QUEUE")}
        </Button>
      </div>
      <div className="queue-list-table-container">
        <div className="queue-list-table-wrapper">
          <Table sortable className="queue-list-table-style">
            <TableHeader>
              <TableRow className="queue-list-header-style">
                <TableHeaderCell className={`queue-list-cell-bold ${filterVisible.NAME ? 'queue-list-expanded-column' : ''}`} {...headerSortProps("NAME")}>
                  <div className="queue-list-header-filter-container">
                    <div className="queue-list-div-filter-style">
                      {t("NAME")}
                      <CiFilter 
                        className="queue-list-filter-icon"
                        onClick={e => { e.stopPropagation(); toggleFilterVisibility("NAME"); }} 
                      />
                      {filterVisible.NAME && (
                        <Input 
                          className="queue-list-input-filter-inline"
                          value={filters.NAME} 
                          onClick={e => e.stopPropagation()}
                          onChange={(e, data) => handleFilterChange("NAME", data.value)} 
                          placeholder="Filtrer"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className={`queue-list-cell-bold ${filterVisible.ENTITY ? 'queue-list-expanded-column' : ''}`} {...headerSortProps("ENTITY")}>
                  <div className="queue-list-header-filter-container">
                    <div className="queue-list-div-filter-style">
                          {t("SCOPE")}
                      <CiFilter 
                        className="queue-list-filter-icon"
                        onClick={e => { e.stopPropagation(); toggleFilterVisibility("ENTITY"); }} 
                      />
                      {filterVisible.ENTITY && (
                        <Input 
                          className="queue-list-input-filter-inline"
                          value={filters.ENTITY} 
                          onClick={e => e.stopPropagation()}
                          onChange={(e, data) => handleFilterChange("ENTITY", data.value)} 
                          placeholder="Filtrer"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>
                 <TableHeaderCell className={`queue-list-cell-bold ${filterVisible.AGENT_HUNT_STRATEGY ? 'queue-list-expanded-column' : ''}`} {...headerSortProps("AGENT_HUNT_STRATEGY")}>
                  <div className="queue-list-header-filter-container">
                    <div className="queue-list-div-filter-style">
                       {t("AGENT_HUNT_STRATEGY")}
                       <InfoButton infoKey="AGENT_HUNT_STRATEGY" category="QUEUES" />
                      <CiFilter 
                        className="queue-list-filter-icon"
                        onClick={e => { e.stopPropagation(); toggleFilterVisibility("AGENT_HUNT_STRATEGY"); }} 
                      />
                      {filterVisible.AGENT_HUNT_STRATEGY && (
                        <Input 
                          className="queue-list-input-filter-inline"
                          value={filters.AGENT_HUNT_STRATEGY} 
                          onClick={e => e.stopPropagation()}
                          onChange={(e, data) => handleFilterChange("AGENT_HUNT_STRATEGY", data.value)} 
                          placeholder="Filtrer"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className={`queue-list-cell-bold ${filterVisible.QUEUE_SIZE ? 'queue-list-expanded-column' : ''}`} {...headerSortProps("QUEUE_SIZE")}>
                  <div className="queue-list-header-filter-container">
                    <div className="queue-list-div-filter-style">
                       {t("QUEUE_SIZE")}
                       <InfoButton infoKey="QUEUE_SIZE" category="QUEUES" />
                      <CiFilter 
                        className="queue-list-filter-icon"
                        onClick={e => { e.stopPropagation(); toggleFilterVisibility("QUEUE_SIZE"); }} 
                      />
                      {filterVisible.QUEUE_SIZE && (
                        <Input 
                          className="queue-list-input-filter-inline"
                          value={filters.QUEUE_SIZE} 
                          onClick={e => e.stopPropagation()}
                          onChange={(e, data) => handleFilterChange("QUEUE_SIZE", data.value)} 
                          placeholder="Filtrer"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>
                  <TableHeaderCell className={`queue-list-cell-bold ${filterVisible.MAX_WAIT_TIME ? 'queue-list-expanded-column' : ''}`} {...headerSortProps("MAX_WAIT_TIME")}>
                  <div className="queue-list-header-filter-container">
                    <div className="queue-list-div-filter-style">
                       {t("MAX_WAIT_TIME")}
                      <CiFilter 
                        className="queue-list-filter-icon"
                        onClick={e => { e.stopPropagation(); toggleFilterVisibility("MAX_WAIT_TIME"); }} 
                      />
                      {filterVisible.MAX_WAIT_TIME && (
                        <Input 
                          className="queue-list-input-filter-inline"
                          value={filters.MAX_WAIT_TIME} 
                          onClick={e => e.stopPropagation()}
                          onChange={(e, data) => handleFilterChange("MAX_WAIT_TIME", data.value)} 
                          placeholder="Filtrer"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>
                  <TableHeaderCell className={`queue-list-cell-bold ${filterVisible.NUMBER_OF_AGENT ? 'queue-list-expanded-column' : ''}`} {...headerSortProps("NUMBER_OF_AGENT")}>
                  <div className="queue-list-header-filter-container">
                    <div className="queue-list-div-filter-style">
                       {t("NUMBER_OF_AGENT")}
                      <CiFilter 
                        className="queue-list-filter-icon"
                        onClick={e => { e.stopPropagation(); toggleFilterVisibility("NUMBER_OF_AGENT"); }} 
                      />
                      {filterVisible.NUMBER_OF_AGENT && (
                        <Input 
                          className="queue-list-input-filter-inline"
                          value={filters.NUMBER_OF_AGENT} 
                          onClick={e => e.stopPropagation()}
                          onChange={(e, data) => handleFilterChange("NUMBER_OF_AGENT", data.value)} 
                          placeholder="Filtrer"
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="queue-list-cell-bold queue-list-cell-right">Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((queue, index) =>{
                if(!queue.item.permissions.main.canRead) return null;
                return(
                <TableRow key={queue.item.id}>
                                   <TableCell>
                                     {" "}
                                     <TableCellLayout>{queue.item.queueName}</TableCellLayout>
                                   </TableCell>
                                   <TableCell>
                                     {" "}
                                     <TableCellLayout>{queue.item.scope}</TableCellLayout>
                                   </TableCell>
                                   <TableCell>
                                     <TableCellLayout>{queue.item.huntMethod}</TableCellLayout>
                                   </TableCell>
                                   <TableCell>
                                     <TableCellLayout>
                                       {queue.item.maxInQueue || "0"}
                                     </TableCellLayout>
                                   </TableCell>
                                   <TableCell>
                                     <TableCellLayout>
                                       {queue.item.maxWaitTime}
                                     </TableCellLayout>
                                   </TableCell>
                                   <TableCell>
                                     {" "}
                                     <TableCellLayout>
                                       {queue.item.numberOfAgents || "0"}
                                     </TableCellLayout>
                                   </TableCell>
                                   <TableCell className="queue-list-cell-right">
                                     <Button
                                       appearance="subtle"
                                       icon={loadingDeleteId === queue.item?.id ? <Spinner size="tiny" /> : <DeleteRegular />}
                                       aria-label="Delete"
                                       disabled={!queue.item?.permissions?.main?.canDelete || loadingDeleteId === queue.item?.id}
                                       onClick={() => confirmRemoveQueue(queue.item)}
                                     />
               
                                     <Button
                                       appearance="subtle"
                                       icon={<EditRegular />}
                                       aria-label="Edit"
                                       disabled={!queue.item?.permissions?.main?.canUpdate}
                                       onClick={() => selectQueue(queue.item)}
                                     />
                                   </TableCell>
                                 </TableRow>
              )} )}
            </TableBody>
          </Table>
        </div>
      </div>

      {selectedQueue && (
        <ConfirmAlert
          open={!!selectedQueue}
          name={selectedQueue.queueName}
          onConfirm={() => removeQueueHandler(selectedQueue)}
          onCancel={() => setSelectedQueue(null)}
        />
      )}
      </div>
    </>
  );
};

export default ListQueuesComponent;
